import { useCallback, useContext, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { Layout, Card, Text, Box } from '@shopify/polaris';
import ProgramHeader from '#client/pages/Programs/Program/components/ProgramHeader/ProgramHeader';
import ProgramInfo from '#client/pages/Programs/Program/components/ProgramInfo/ProgramInfo';
import ProgramDetails from '#client/pages/Programs/Program/components/ProgramDetails/ProgramDetails';
import ProductTable from '#client/pages/Programs/Program/components/ProductTable/ProductTable';
import ProgramMetrics from '#client/pages/Programs/Program/components/ProgramMetrics/ProgramMetrics';
import MultiProductFilter from '#client/components/features/MultiProductFilter/MultiProductFilter';
import ProgramPageWrapper from './ProgramPageWrapper';
import ProgramBreadcrumbs from './ProgramBreadcrumbs';
import ProgramActionButtons from './ProgramActionButtons';
import { useProgramToast, useProgramActions, useProgramStore } from '#client/stores/program/useProgramStore';
import { useProductFilterStore, useProductFilterActions } from '#client/stores/productFilter/useProductFilterStore';
import { SessionContext } from '#client/contexts/SessionContext';
import { useToast } from '#client/components/common/Toast/ToastContext';

const Program = () => {
    const navigate = useNavigate();
    const { id: routeProgramId } = useParams();

    const { pathname } = useLocation();

    const { isCustomerAuthenticated } = useContext(SessionContext);
    const isCustomer = isCustomerAuthenticated;
    // Zustand store hooks
    const { setMode, initializeWithProgram, setProgramId: setProgramIdAction, setName, setCompanyName, setStatus } = useProgramActions();
    const { toastError, setToastError } = useProgramToast();
    const { addToast } = useToast();
    const restrictToEligibleProducts = useProgramStore.use.restrictToEligibleProducts();
    const { setSnapshotId } = useProductFilterActions();

    // Show program store errors, such as a failed save or "No changes to save.", once each
    useEffect(() => {
        if (!toastError) return;
        addToast({ type: 'error', message: toastError });
        setToastError(null);
    }, [toastError, addToast, setToastError]);

    // Clear snapshot when switching from filter mode to 'All Items'
    useEffect(() => {
        if (!restrictToEligibleProducts) {
            // Only clear snapshot, don't reset the entire filter (which would reset restrictToEligibleProducts back to true)
            setSnapshotId?.(null);
        }
    }, [restrictToEligibleProducts, setSnapshotId]);

    // Determine the mode based on the current pathname
    const getProgramMode = useCallback(() => {
        if (pathname.includes('/create')) return 'create';
        if (pathname.includes('/edit')) return 'edit';
        return 'view';
    }, [pathname]);
    let routeId = parseInt(routeProgramId, 10);
    // Initialize program state based on the route
    useEffect(() => {
        let initialMode = getProgramMode();

        if (routeId && initialMode !== 'create') {
            // ---- CUSTOMER PATH (view-only) ----

            // ---- STAFF PATH ----
            (async () => {
                try {
                    // Always initialize with the new routeId and mode
                    await initializeWithProgram(routeId, initialMode);
                } catch (error) {
                    // Shown directly: this page unmounts as it navigates away, before a store error could be shown
                    addToast({ type: 'error', message: error?.message || 'Failed to initialize program' });
                    navigate('/program-management', { viewTransition: true });
                }
            })();
        }
        if (initialMode === 'create') {
            setMode('create');
            // Reset product filter store when creating a new program
            useProductFilterStore.getState().resetProductFilter();
        }
    }, [isCustomer, routeId, getProgramMode, initializeWithProgram, setMode, addToast, setProgramIdAction, setName, setCompanyName, setStatus]);

    return (
        <>
            <ProgramBreadcrumbs />
            <ProgramPageWrapper>
                <Layout>
                    <Layout.Section>
                        <ProgramHeader />
                    </Layout.Section>
                    {!isCustomer && (
                        <Layout.Section>
                            <ProgramMetrics />
                        </Layout.Section>
                    )}
                    <Layout.Section>
                        <ProgramInfo />
                    </Layout.Section>
                    <Layout.Section>
                        <ProgramDetails />
                    </Layout.Section>
                    {!isCustomer && (
                        <Layout.Section>
                            <Card>
                                <Box paddingBlock="400">
                                    <Box paddingBlock="400">
                                        <Text variant="headingLg">
                                            Eligible Product Rules
                                            <Box as="span" paddingInlineStart="200">
                                                <Text as="span" variant="bodyMd" tone="critical">
                                                    *
                                                </Text>
                                            </Box>
                                        </Text>
                                    </Box>
                                    <MultiProductFilter />
                                </Box>
                            </Card>
                        </Layout.Section>
                    )}
                    {restrictToEligibleProducts && (
                        <Layout.Section>
                            <ProductTable programId={routeId} />
                        </Layout.Section>
                    )}
                    <Layout.Section>
                        <ProgramActionButtons />
                    </Layout.Section>
                </Layout>
            </ProgramPageWrapper>
        </>
    );
};

export default Program;
